import { Dispatch, SetStateAction } from 'react';

import {
  ICalendarEventType,
  IMatchingAvailabilitiesData,
  IMatchingAvailabilityChildren,
  ServiceTypes,
  TimeFormat,
} from '@pharmaplan/common';

import ServiceHandler, {
  idStore,
} from '@pharmaplan/common/helpers/ServiceHandler';
import { stub } from '@pharmaplan/common/helpers/utils';
import {
  DynamicTableCell,
  nameLabels,
  OtherScreens,
  ScreenTypes,
} from '../../../helpers/Constants';
import addAvailablilityIcon from '../../../assets/svg/addAvailabilityIcon.svg';
import approveBookingIcon from '../../../assets/svg/approveBooking.svg';
import deleteIcon from '../../../assets/svg/delete.svg';
import { renderScreen } from '../../../actions/drawerActions';
import { getDayMonthDateYear } from '../../Reports/helpers';
import { timeString } from '../../../helpers/Functions';
import strings from '../../../localization';
import { store } from '../../../store';
import { dateAndTime } from '../AdminReports/helper';

const isSelfService = ServiceTypes.self === ServiceHandler.getService();
const keys = {
  time: '2',
  action: '3',
};

export interface ITableRowConfig {
  data: Array<IMatchingAvailabilitiesData>;
  timeFormat: TimeFormat;
  dispatch: typeof store.dispatch;
  workshiftId: string;
  type: ICalendarEventType;
  back: () => void;
  showDeletePreSubmit: (availabilityId: string, dateTime: string) => void;
  canMakeBooking?: boolean;
  isLongTerm: boolean;
  openModal?: (
    children: Array<IMatchingAvailabilityChildren>,
    name: string,
    title: string
  ) => () => void;
}

export const tableHeaderConfig = (isLongTerm: boolean) => {
  let arr = [
    { key: '0', label: nameLabels().roleLabel },
    { key: '1', label: strings.date },
    { key: keys.time, label: strings.time },
    { key: keys.action, label: strings.action },
  ];

  if (isLongTerm) {
    arr = arr.filter((item) =>
      ![keys.time, keys.action].includes(item.key));
  }

  return arr;
};

export const tableRowConfig = ({
  data,
  timeFormat,
  dispatch,
  workshiftId,
  type,
  back,
  showDeletePreSubmit,
  canMakeBooking,
  isLongTerm,
  openModal,
}: ITableRowConfig) =>
  data?.map((item) => {
    const {
      pharmacistName,
      startDate,
      professionalName,
      endDate,
      availableTimeId,
      selectedDate,
      children,
      isRequested,
    } = item ?? {};

    const id = item[idStore.id as keyof typeof item] as string;

    const time = timeString(startDate, endDate, timeFormat);
    const date = getDayMonthDateYear(selectedDate);

    const sDate = getDayMonthDateYear(startDate);
    const eDate = getDayMonthDateYear(endDate);

    const dateTime = dateAndTime(selectedDate, startDate, endDate, timeFormat);
    const name = isSelfService ? professionalName : pharmacistName;

    let dateObj: {
      key: string;
      maxWidth?: number;
      value:
        | string
        | {
            label: string;
            handlePress: () => void;
          };
      type: DynamicTableCell;
    } = {
      key: `1-${availableTimeId}`,
      value: date,
      type: DynamicTableCell.smallerText,
    };

    let tooltipConfig = {
      icon: addAvailablilityIcon,
      tooltip: strings.book,
    };

    if (isRequested) {
      tooltipConfig = {
        icon: approveBookingIcon,
        tooltip: strings.approve,
      };
    }

    if (isLongTerm && children.length === 1) {
      dateObj = {
        key: `1-${availableTimeId}`,
        value: dateAndTime(
          children[0].startDate,
          children[0].startDate,
          children[0].endDate,
          timeFormat,
        ),
        maxWidth: 200,
        type: DynamicTableCell.smallerText,
      };
    } else if (isLongTerm) {
      const range = `${sDate} - ${eDate}`;
      dateObj = {
        key: `1-${availableTimeId}`,
        value: {
          label: range,
          handlePress: openModal?.(children, name ?? '', range) ?? stub,
        },
        maxWidth: 200,
        type: DynamicTableCell.smallerCustomLink,
      };
    }

    return {
      key: `${availableTimeId}`,
      data: [
        {
          key: `0-${availableTimeId}`,
          value: {
            label: name,
            handlePress: () => {
              dispatch(
                renderScreen({
                  screenNumber: 2,
                  screenType: ScreenTypes.profile,
                  pharmacistId: id,
                  fromScreen: OtherScreens.MatchingAvailabilities,
                  type,
                  back,
                }),
              );
            },
          },
          type: DynamicTableCell.smallerCustomLink,
        },
        dateObj,
        !isLongTerm && {
          key: `2-${availableTimeId}`,
          value: time,
          type: DynamicTableCell.smallerText,
        },
        !isLongTerm && {
          key: `3-${availableTimeId}`,
          show: true,
          value: [
            {
              key: '1',
              icon: tooltipConfig.icon,
              tooltip: tooltipConfig.tooltip,
              disabled: !canMakeBooking,
              onClick: () => {
                dispatch(
                  renderScreen({
                    screenNumber: 4,
                    screenType: ScreenTypes.requestedList,
                    fromScreen: OtherScreens.MatchingAvailabilities,
                    eventId: availableTimeId,
                    workshiftId,
                    type,
                  }),
                );
              },
            },
            {
              key: '2',
              icon: deleteIcon,
              tooltip: strings.delete,
              onClick: () => {
                showDeletePreSubmit(availableTimeId, dateTime);
              },
            },
          ],
          type: DynamicTableCell.iconButton,
        },
      ],
    };
  });

export const handleClick = (
  setShowOtherAvailabilities: Dispatch<SetStateAction<boolean>>,
  state: boolean,
) =>
  () => {
    setShowOtherAvailabilities(state);
  };
